import React, { useState, useEffect } from 'react';
import {
  Card, Space, Button, Tag, Descriptions, Statistic, Row, Col, 
  message, Typography, Divider, Popconfirm, Alert, InputNumber, Form
} from 'antd';
import {
  ReloadOutlined, DeleteOutlined, DatabaseOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined, WarningOutlined
} from '@ant-design/icons';
import { useI18n } from '../hooks/useI18n';
import { ibuildProxyService } from '../services/ibuildProxyService';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

interface CleanupStats {
  daysBefore: number;
  cutoffDate: string;
  estimatedDeletions: {
    requestMappings: number;
    responseMessages: number;
    streamMessages: number;
    auditEvents: number;
    total: number;
  };
}

interface CleanupResult {
  daysBefore: number;
  cutoffDate: string;
  deleted: {
    requestMappings: number;
    responseMessages: number;
    streamMessages: number;
    auditEvents: number;
    total: number;
  };
}

const IBuildProxyCleanupManagement: React.FC = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [stats, setStats] = useState<CleanupStats | null>(null);
  const [lastCleanupResult, setLastCleanupResult] = useState<CleanupResult | null>(null);
  const [daysBefore, setDaysBefore] = useState<number>(30);
  const [form] = Form.useForm();

  useEffect(() => {
    loadCleanupStats();
  }, []);

  const loadCleanupStats = async (days: number = daysBefore) => {
    try {
      setLoading(true);
      const result = await ibuildProxyService.getCleanupStats(days);
      
      if (result.success && result.data) {
        setStats(result.data);
        setDaysBefore(days);
        form.setFieldsValue({ daysBefore: days });
      } else {
        message.error(`加载清理统计信息失败: ${result.error || result.message}`);
      }
    } catch (error: any) {
      message.error(`加载清理统计信息失败: ${error.message}`);
      console.error('清理统计信息加载失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    try {
      const values = await form.validateFields();
      const days = values.daysBefore || daysBefore;
      
      setCleanupLoading(true);
      const result = await ibuildProxyService.cleanup(days);
      
      if (result.success) {
        message.success(result.message || `成功清理 ${result.data?.deleted?.total || 0} 条记录`);
        setLastCleanupResult(result.data);
        // 重新加载统计信息
        await loadCleanupStats(days);
      } else {
        message.error(result.error || result.message || '清理操作失败');
      }
    } catch (error: any) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      message.error(`清理操作失败: ${error.message}`);
      console.error('清理操作失败:', error);
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleDaysChange = (value: number | null) => {
    if (value !== null && value > 0) {
      loadCleanupStats(value);
    }
  };

  const totalEstimated = stats?.estimatedDeletions.total || 0;
  const hasDataToClean = totalEstimated > 0;

  return (
    <div style={{ padding: '20px' }}>
      <Card
        title={
          <Space>
            <DatabaseOutlined />
            <span>IBuild Proxy 数据清理管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadCleanupStats(daysBefore)}
              loading={loading}
            >
              刷新统计
            </Button>
          </Space>
        }
      >
        {/* 清理参数配置 */}
        <Card title="清理参数配置" size="small" style={{ marginBottom: 24 }}>
          <Form
            form={form}
            layout="inline"
            initialValues={{ daysBefore: 30 }}
            onFinish={handleCleanup}
          >
            <Form.Item
              label="清理天数"
              name="daysBefore"
              rules={[
                { required: true, message: '请输入清理天数' },
                { type: 'number', min: 1, message: '清理天数必须大于0' }
              ]}
            >
              <InputNumber
                min={1}
                max={365}
                style={{ width: 120 }}
                onChange={handleDaysChange}
                placeholder="天数"
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  icon={<DeleteOutlined />}
                  htmlType="submit"
                  loading={cleanupLoading}
                  danger={hasDataToClean}
                >
                  执行清理
                </Button>
                <Button
                  onClick={() => loadCleanupStats(form.getFieldValue('daysBefore') || 30)}
                  loading={loading}
                >
                  查询统计
                </Button>
              </Space>
            </Form.Item>
          </Form>
          {stats && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                将清理 {stats.daysBefore} 天前的数据（截止日期: {dayjs(stats.cutoffDate).format('YYYY-MM-DD HH:mm:ss')}）
              </Text>
            </div>
          )}
        </Card>

        {/* 清理统计信息 */}
        {stats && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="请求映射"
                    value={stats.estimatedDeletions.requestMappings}
                    prefix={<DatabaseOutlined />}
                    valueStyle={{ fontSize: '24px' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="响应消息"
                    value={stats.estimatedDeletions.responseMessages}
                    prefix={<DatabaseOutlined />}
                    valueStyle={{ fontSize: '24px' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="流式消息"
                    value={stats.estimatedDeletions.streamMessages}
                    prefix={<DatabaseOutlined />}
                    valueStyle={{ fontSize: '24px' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="审计事件"
                    value={stats.estimatedDeletions.auditEvents}
                    prefix={<DatabaseOutlined />}
                    valueStyle={{ fontSize: '24px' }}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <Card>
                  <Statistic
                    title="预计删除总数"
                    value={stats.estimatedDeletions.total}
                    prefix={<ExclamationCircleOutlined />}
                    valueStyle={{ 
                      fontSize: '32px',
                      color: stats.estimatedDeletions.total > 0 ? '#cf1322' : '#3f8600'
                    }}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* 警告提示 */}
        {stats && hasDataToClean && (
          <Alert
            message="检测到可清理的数据"
            description={`预计将删除 ${stats.estimatedDeletions.total} 条记录，此操作不可恢复，请谨慎操作！`}
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        {stats && !hasDataToClean && (
          <Alert
            message="暂无需要清理的数据"
            description={`${stats.daysBefore} 天前的数据已全部清理，或没有符合条件的数据。`}
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        {/* 上次清理结果 */}
        {lastCleanupResult && (
          <>
            <Divider />
            <Card title="上次清理结果" size="small" style={{ marginBottom: 24 }}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="清理天数">
                  {lastCleanupResult.daysBefore} 天
                </Descriptions.Item>
                <Descriptions.Item label="截止日期">
                  {dayjs(lastCleanupResult.cutoffDate).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label="删除的请求映射">
                  <Tag color="blue">{lastCleanupResult.deleted.requestMappings}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="删除的响应消息">
                  <Tag color="green">{lastCleanupResult.deleted.responseMessages}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="删除的流式消息">
                  <Tag color="purple">{lastCleanupResult.deleted.streamMessages}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="删除的审计事件">
                  <Tag color="orange">{lastCleanupResult.deleted.auditEvents}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="删除总数" span={2}>
                  <Tag color="red" style={{ fontSize: '16px', padding: '4px 12px' }}>
                    {lastCleanupResult.deleted.total}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </>
        )}

        {/* 详细统计信息 */}
        {stats && (
          <Card title="详细统计信息" size="small">
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="清理天数">
                {stats.daysBefore} 天
              </Descriptions.Item>
              <Descriptions.Item label="截止日期">
                {dayjs(stats.cutoffDate).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="请求映射（预计删除）">
                <Tag color="blue">{stats.estimatedDeletions.requestMappings}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="响应消息（预计删除）">
                <Tag color="green">{stats.estimatedDeletions.responseMessages}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="流式消息（预计删除）">
                <Tag color="purple">{stats.estimatedDeletions.streamMessages}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="审计事件（预计删除）">
                <Tag color="orange">{stats.estimatedDeletions.auditEvents}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="预计删除总数" span={2}>
                <Tag color="red" style={{ fontSize: '16px', padding: '4px 12px' }}>
                  {stats.estimatedDeletions.total}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Card>
    </div>
  );
};

export default IBuildProxyCleanupManagement;

